<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import * as ActiveStorage from 'activestorage';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

const props = defineProps({
  roomId: {
    type: Number,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['send']);

const { t } = useI18n();

const message = ref('');
const attachedFiles = ref([]);
const showEmojiPicker = ref(false);
const isFocused = ref(false);
const messageEditor = ref(null);
const updateEditorSelectionWith = ref('');

const hasAttachments = computed(() => attachedFiles.value.length > 0);
const isSendDisabled = computed(
  () =>
    props.disabled ||
    ((!message.value || !message.value.trim()) && !hasAttachments.value)
);

const replyButtonLabel = computed(() => {
  return `${t('INTERNAL_CHAT.SEND')} (↵)`;
});

onMounted(() => {
  ActiveStorage.start();
});

const toggleEmojiPicker = () => {
  showEmojiPicker.value = !showEmojiPicker.value;
};

const hideEmojiPicker = () => {
  showEmojiPicker.value = false;
};

const onFocus = () => {
  isFocused.value = true;
};

const onBlur = () => {
  isFocused.value = false;
};

const onFileUpload = fileData => {
  const { file } = fileData;
  if (!file) return;
  const reader = new FileReader();
  reader.readAsDataURL(file.file || file);
  reader.onloadend = () => {
    attachedFiles.value.push({
      resource: file.file || file,
      thumb: reader.result,
      isRecordedAudio: fileData.isRecordedAudio || false,
    });
  };
};

const addIntoEditor = content => {
  updateEditorSelectionWith.value = content;
  onFocus();
  hideEmojiPicker();
};

const onPaste = e => {
  if (props.disabled) return;
  Array.from(e.clipboardData.files)
    .filter(file => file.size > 0)
    .forEach(file =>
      onFileUpload({ name: file.name, type: file.type, size: file.size, file })
    );
};

const removeAttachment = attachments => {
  attachedFiles.value = attachments;
};

const onSend = () => {
  if (isSendDisabled.value) return;
  emit('send', {
    content: message.value.trim(),
    files: attachedFiles.value.map(a => a.resource),
  });
  message.value = '';
  attachedFiles.value = [];
};

useKeyboardEvents({
  Enter: {
    action: e => {
      if (isFocused.value && !showEmojiPicker.value && !isSendDisabled.value) {
        e.preventDefault();
        onSend();
      }
    },
    allowOnFocusedInput: true,
  },
});
</script>

<template>
  <div class="border-t border-n-weak bg-n-background">
    <div class="reply-box" @paste="onPaste">
      <EmojiInput
        v-if="showEmojiPicker"
        v-on-clickaway="hideEmojiPicker"
        :on-click="addIntoEditor"
      />
      <div class="reply-box__top px-3 pt-3">
        <WootMessageEditor
          ref="messageEditor"
          v-model="message"
          :editor-id="`internal-${roomId}`"
          class="input popover-prosemirror-menu"
          :placeholder="t('INTERNAL_CHAT.PLACEHOLDER')"
          :disabled="disabled"
          :enable-canned-responses
          :enable-variables="false"
          :allow-signature="false"
          :override-line-breaks
          :focus-on-mount="false"
          :update-selection-with="updateEditorSelectionWith"
          @focus="onFocus"
          @blur="onBlur"
        />
        <div v-if="hasAttachments" class="py-2">
          <AttachmentPreview
            :attachments="attachedFiles"
            @remove-attachment="removeAttachment"
          />
        </div>
      </div>
      <ReplyBottomPanel
        :conversation-id="roomId"
        :enable-multiple-file-upload
        :inbox="{}"
        :is-on-private-note
        :is-note
        :is-send-disabled="isSendDisabled"
        :on-file-upload="onFileUpload"
        :on-send="onSend"
        :send-button-text="replyButtonLabel"
        :show-audio-recorder="false"
        :show-emoji-picker="showEmojiPicker"
        :show-file-upload
        :message="message"
        portal-slug=""
        @toggle-emoji-picker="toggleEmojiPicker"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.reply-box {
  @apply relative mb-2 mx-2 border border-n-weak rounded-xl bg-n-solid-1;

  &.is-private {
    @apply bg-n-solid-amber;
  }
}
</style>
